import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';

import { ContextualMenuItemType, Stack } from '@fluentui/react';
import { IconButton } from '@fluentui/react/lib/Button';

// Interfaces > Utils > Hooks > ClassNames
import { Property } from '../../../api-client';
import { RootState } from '../../../redux/application/redux-store';
import { SortingComparer } from '../../../utils/data-comparer';
import { DateTimeEnchanter } from '../../../utils/data-enchanters';

import { useProperties } from './hooks/useProperties';
import { useClassNames } from './properties.classNames';

import { IAIApplicationLayout } from '../../../common/layouts/IAIApplicationLayout';
import { IAICalloutColumn } from '../../../common/components/IAIDataTable/components/IAIColumns/IAICalloutColumn';
import { IAIDataTable } from '../../../common/components/IAIDataTable';
import { IAIDialog } from '../../../common/components/IAIDialog';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_KEYS } from './Localization/translation-keys';
import AttributeTypeColumn from './components/AttributeTypeColumn/attribute-type-column';
import FormDrawer from './components/FormDrawer/form-drawer';

const PropertiesPage: React.FunctionComponent = () => {
    const {
        handleProperties,
        properties,
        propertiesLoading,
        handleCreateProperties,
        createPropertiesLoading,
        handleRetrieveProperties,
        retrieveProperties,
        retrievePropertiesLoading,
        handleUpdateProperties,
        updatePropertiesLoading,
        handleDeleteProperties,
        deletePropertiesLoading
    } = useProperties();

    const {
        pageContainer,
        calloutSecondaryHeading,
        calloutText
    } = useClassNames();

    useEffect(() => {
        handleProperties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const iaiSystem = useSelector((state: RootState) => state.system);

    const _handleSearchProperties = (entry: Property, searchValue: string): boolean => (
        entry.displayName?.toLowerCase().search(searchValue) !== -1
    );

    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

    const _handleSubmit = (customAttribute: Property) => {
        !customAttribute.id
            ?
            handleCreateProperties({
                customAttribute,
                onSuccess: () => {
                    setDrawerVisible(false);
                    handleProperties();
                }
            })
            :
            handleUpdateProperties({
                id: customAttribute.id,
                customAttribute,
                onSuccess: () => {
                    setDrawerVisible(false);
                    handleProperties();
                }
            });
    }

    const {
        t: translate
    } = useTranslation();

    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
    const [delegateEntity, setDelegateEntity] = useState<Property | undefined>(undefined);

    return (
        <IAIApplicationLayout
            icon={'AllApps'}
            heading={translate(TRANSLATION_KEYS.Heading)}
            secondaryHeading={translate(TRANSLATION_KEYS.SecondaryHeading)}
        >
            <div className={pageContainer}>
                <IAIDataTable
                    columns={[
                        {
                            key: 'preview',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (element: any) => (
                                <IAICalloutColumn
                                    title={element.displayName}
                                    tooltipContent={translate(TRANSLATION_KEYS.TooltipContentTitle)}
                                    contentLoading={retrievePropertiesLoading}
                                    content={
                                        retrieveProperties &&
                                        <div className="col-12">
                                            <span className={calloutSecondaryHeading}>
                                                {`${translate(TRANSLATION_KEYS.TooltipContent)} ${DateTimeEnchanter.formatStandardDate(retrieveProperties.createdAt, 'it-IT')}`}
                                            </span>
                                            <hr />
                                            <span className={calloutText}>
                                                {_.map(retrieveProperties.options, (value, dataIndex) => (
                                                    <div key={dataIndex}>{value}</div>
                                                ))}
                                            </span>
                                        </div>
                                    }
                                    onEngage={() => handleRetrieveProperties({
                                        id: element.id,
                                        onRetrieveSuccess: (properties) => setDelegateEntity(properties)
                                    })}
                                    onDismiss={() => setDelegateEntity(undefined)}
                                />
                            )
                        },
                        {
                            key: 'label',
                            name: translate(TRANSLATION_KEYS.ColumnAttributeLabel),
                            minWidth: 150,
                            isResizable: true,
                            sorter: SortingComparer.stringComparer,
                            onRender: (element: Property) => (
                                <Stack verticalAlign="center" verticalFill>
                                    <span>{element.label}</span>
                                </Stack>
                            )
                        },
                        {
                            key: 'attributeType',
                            name: translate(TRANSLATION_KEYS.ColumnAttributeType),
                            minWidth: 150,
                            isResizable: true,
                            onRender: (element: Property) => (
                                <AttributeTypeColumn align="center" type={element.attributeType} />
                            )
                        },
                        {
                            key: 'area',
                            name: translate(TRANSLATION_KEYS.ColumnAreaLabel),
                            minWidth: 150,
                            isResizable: true,
                            sorter: SortingComparer.stringComparer,
                            onRender: (element: Property) => (
                                <Stack verticalAlign="center" verticalFill>
                                    <span>
                                        {_.find(iaiSystem.areas,
                                            x => x.id === element.areaId)?.displayName || 'N/A'}
                                    </span>
                                </Stack>
                            )
                        },
                        {
                            key: 'department',
                            name: translate(TRANSLATION_KEYS.ColumnDepartmentLabel),
                            minWidth: 150,
                            isResizable: true,
                            sorter: SortingComparer.stringComparer,
                            onRender: (element: Property) => (
                                <Stack verticalAlign="center" verticalFill>
                                    <span>
                                        {_.find(iaiSystem.departments,
                                            x => x.id === element.departmentId)?.displayName || 'N/A'}
                                    </span>
                                </Stack>
                            )
                        },
                        {
                            key: 'defaultRender',
                            name: translate(TRANSLATION_KEYS.RenderAttribute),
                            minWidth: 150,
                            isResizable: true,
                            onRender: (element: Property) => (
                                <div className="text-center">
                                    <IconButton
                                        iconProps={
                                            {
                                                iconName: element.defaultRender
                                                    ? 'CheckboxCompositeReversed'
                                                    : 'Checkbox'
                                            }
                                        }
                                    />
                                </div>
                            )
                        },
                        {
                            key: 'property_action__',
                            fieldName: '',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (element: any, dataIndex?: number) => (
                                <div className="text-center">
                                    <IconButton
                                        menuIconProps={{ iconName: 'CollapseMenu' }}
                                        menuProps={{
                                            items: [
                                                {
                                                    key: `${dataIndex}_menu_header__`,
                                                    itemType: ContextualMenuItemType.Header,
                                                    text: translate(TRANSLATION_KEYS.TableEditActions)
                                                },
                                                {
                                                    key: `${dataIndex}_menu_update__`,
                                                    iconProps: { iconName: 'EditCreate' },
                                                    text: translate(TRANSLATION_KEYS.TableEditEdit),
                                                    onClick: () => {
                                                        handleRetrieveProperties({
                                                            id: element.id,
                                                            onRetrieveSuccess: (properties) => {
                                                                setDelegateEntity(properties);
                                                                setDrawerVisible(true);
                                                            }
                                                        });
                                                    }
                                                },
                                                {
                                                    key: `${dataIndex}_menu_delete__`,
                                                    iconProps: { iconName: 'Delete' },
                                                    text: translate(TRANSLATION_KEYS.TableEditDelete),
                                                    onClick: () => {
                                                        setDelegateEntity(element);
                                                        setDeleteDialogVisible(true);
                                                    }
                                                }
                                            ]
                                        }}
                                    />
                                </div>
                            )
                        }
                    ]}
                    dataSource={properties || []}
                    dataLoading={propertiesLoading}
                    dataTableCommands={[{
                        disabled: propertiesLoading,
                        text: translate(TRANSLATION_KEYS.NewButtonText),
                        iconProps: { iconName: 'Add' },
                        onClick: () => {
                            setDelegateEntity(undefined)
                            setDrawerVisible(true)
                        }
                    }]}
                    searchable
                    onSearch={_handleSearchProperties}
                />

                <FormDrawer
                    customAttribute={delegateEntity}
                    disabled={(
                        retrievePropertiesLoading ||
                        createPropertiesLoading ||
                        updatePropertiesLoading
                    )}
                    drawerVisible={drawerVisible}
                    onDrawerConfirm={_handleSubmit}
                    onDrawerDismiss={() => { setDrawerVisible(false); }}
                />

                <IAIDialog
                    title={`${translate(TRANSLATION_KEYS.DeleteDialogHeading)} ${delegateEntity?.label}`}
                    subText={translate(TRANSLATION_KEYS.DeleteDialogSecondaryHeading)}
                    disabled={deletePropertiesLoading || retrievePropertiesLoading}
                    dialogProps={{
                        hidden: !deleteDialogVisible,
                        onDismissed: () => { setDelegateEntity(undefined); }
                    }}
                    confirmText={translate(TRANSLATION_KEYS.DeleteButtonText)}
                    onConfirm={() => {
                        if (!!delegateEntity?.id) {
                            handleDeleteProperties({
                                id: delegateEntity.id,
                                onSuccess: () => {
                                    setDeleteDialogVisible(false);
                                    handleProperties();
                                }
                            });
                        }
                    }}
                    dismissText={translate(TRANSLATION_KEYS.DismissButtonText)}
                    onDismiss={() => setDeleteDialogVisible(false)}
                />
            </div>
        </IAIApplicationLayout>
    );
}

export default PropertiesPage;